import {
  CHANNELS as _CHANNELS,
  DEVICE_TYPES as _DEVICE_TYPES,
  SUPPORTED_LANGUAGES as _SUPPORTED_LANGUAGES,
} from './constants/device.constants';
import {
  ANIMATION_DURATION as _ANIMATION_DURATION,
  DEFAULT_LANGUAGE as _DEFAULT_LANGUAGE,
  DEFAULT_THEME as _DEFAULT_THEME,
  DEFAULT_TIMEZONE as _DEFAULT_TIMEZONE,
  LOADING_DELAY as _LOADING_DELAY,
  PAGE_SIZE as _PAGE_SIZE,
  PAGE_SIZE_OPTIONS as _PAGE_SIZE_OPTIONS,
  SEARCH_TIMEOUT as _SEARCH_TIMEOUT,
  TOAST_DURATION as _TOAST_DURATION,
} from './constants/ui.constants';
import {
  STORAGE_LANGUAGE as _STORAGE_LANGUAGE,
  STORAGE_REFRESH_TOKEN as _STORAGE_REFRESH_TOKEN,
  STORAGE_THEME as _STORAGE_THEME,
  STORAGE_TOKEN_EXPIRES_AT as _STORAGE_TOKEN_EXPIRES_AT,
  STORAGE_USER_INFO as _STORAGE_USER_INFO,
  STORAGE_USER_TOKEN as _STORAGE_USER_TOKEN,
  TOKEN_EXPIRY_TIME as _TOKEN_EXPIRY_TIME,
  TOKEN_REFRESH_THRESHOLD as _TOKEN_REFRESH_THRESHOLD,
} from './constants/auth.constants';

/**
 * 常量管理工具類
 * 所有常量必須在此類中統一定義和管理
 */
export class ConstantUtil {
  // API 相關常量
  //public static readonly API_BASE_URL = 'https://uuspa-server-610955567337.asia-east1.run.app';
  public static readonly API_BASE_URL = 'https://api.uuspa.com.tw';
  public static readonly API_VERSION = 'v1';
  public static readonly API_TIMEOUT = 30000;
  public static readonly API_RETRY_COUNT = 3;
  
  // API 端點
  public static readonly API_USER_LOGIN = '/api/v1/auth/user/login';
  public static readonly API_USER_LOGOUT = '/api/v1/auth/user/logout';
  public static readonly API_USER_PROFILE = '/api/v1/auth/user/profile';
  public static readonly API_USER_UPDATE = '/api/v1/auth/user/update';
  public static readonly API_REFRESH_TOKEN = '/api/v1/auth/user/refresh-token';
  // 地區相關 API
  public static readonly API_GET_REGIONS = '/api/v1/regions/summary';
  public static readonly API_GET_DISTRICTS = (cityKey: string) => `/api/v1/regions/city/${cityKey}/districts`;
  
  // 客服管理 API 端點
  public static readonly API_CUSTOMER_SERVICE_UNUSED_TICKETS = '/api/v1/customer-service/unused-tickets';
  public static readonly API_CUSTOMER_SERVICE_VERIFIED_TICKETS = '/api/v1/customer-service/verified-tickets';
  public static readonly API_CUSTOMER_SERVICE_SUPPLEMENTARY_VERIFICATION = '/api/v1/customer-service/supplementary-verification';
  public static readonly API_CUSTOMER_SERVICE_CANCEL_VERIFICATION = '/api/v1/customer-service/cancel-verification';
  public static readonly API_CUSTOMER_SERVICE_REFUND = '/api/v1/customer-service/refund';
  public static readonly API_CUSTOMER_SERVICE_REFUND_AMOUNT = '/api/v1/customer-service/refund-amount';
  public static readonly API_CUSTOMER_SERVICE_TICKET_SEARCH = '/api/v1/customer-service/tickets/search';
  public static readonly API_CUSTOMER_SERVICE_BATCH_REFUND = '/api/v1/customer-service/batch-refund';
  public static readonly API_CUSTOMER_SERVICE_BATCH_CANCEL_VERIFICATION = '/api/v1/customer-service/batch-cancel-verification';
  public static readonly API_CUSTOMER_SERVICE_BATCH_SUPPLEMENTARY_VERIFICATION = '/api/v1/customer-service/batch-supplementary-verification';
  
  // Storage 鍵名（定義移至 utils/constants/auth.constants.ts）
  public static readonly STORAGE_USER_TOKEN = _STORAGE_USER_TOKEN;
  public static readonly STORAGE_REFRESH_TOKEN = _STORAGE_REFRESH_TOKEN;
  public static readonly STORAGE_TOKEN_EXPIRES_AT = _STORAGE_TOKEN_EXPIRES_AT;
  public static readonly STORAGE_USER_INFO = _STORAGE_USER_INFO;
  public static readonly STORAGE_LANGUAGE = _STORAGE_LANGUAGE;
  public static readonly STORAGE_THEME = _STORAGE_THEME;

  // Token 相關常量（定義移至 utils/constants/auth.constants.ts）
  public static readonly TOKEN_EXPIRY_TIME = _TOKEN_EXPIRY_TIME;
  public static readonly TOKEN_REFRESH_THRESHOLD = _TOKEN_REFRESH_THRESHOLD;
  
  // 分頁相關（定義移至 utils/constants/ui.constants.ts）
  public static readonly PAGE_SIZE = _PAGE_SIZE;
  public static readonly PAGE_SIZE_OPTIONS = _PAGE_SIZE_OPTIONS;

  // UI 常量（定義移至 utils/constants/ui.constants.ts）
  public static readonly TOAST_DURATION = _TOAST_DURATION;
  public static readonly LOADING_DELAY = _LOADING_DELAY;
  public static readonly ANIMATION_DURATION = _ANIMATION_DURATION;
  
  // 業務常量
  public static readonly USER_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    SUSPENDED: 'suspended',
  } as const;
  
  public static readonly ORDER_STATUS = {
    COMPLETED: 'completed',
    INCOMPLETE: 'incomplete',
  } as const;

  public static readonly PAYMENT_STATUS = {
    PAID: 'paid',
    UNPAID: 'unpaid',
    PARTIAL_REFUND: 'partial_refund',
    FULL_REFUND: 'full_refund',
  } as const;

  // 訂單 API 端點
  public static readonly API_ORDERS = '/api/v1/orders';
  public static readonly API_ORDER_BY_ID = (id: string) => `/api/v1/orders/${id}`;
  public static readonly API_ORDER_UPDATE_STATUS = (id: string) => `/api/v1/orders/${id}/status`;
  public static readonly API_ORDER_CANCEL = (id: string) => `/api/v1/orders/${id}/cancel`;
  public static readonly API_ORDER_REFUND = (id: string) => `/api/v1/orders/${id}/refund`;
  public static readonly API_ORDER_STATISTICS = '/api/v1/orders/statistics';
  public static readonly API_ORDER_BATCH_UPDATE = '/api/v1/orders/batch';
  public static readonly API_ORDER_EXPORT = '/api/v1/orders/export';
  public static readonly API_ORDER_HISTORY = (id: string) => `/api/v1/orders/${id}/history`;
  public static readonly API_ORDER_LOGS = (id: string) => `/api/v1/orders/${id}/logs`;
  public static readonly API_ORDER_NOTES = (id: string) => `/api/v1/orders/${id}/notes`;
  public static readonly API_ORDER_NOTE_BY_ID = (orderId: string, noteId: string) => `/api/v1/orders/${orderId}/notes/${noteId}`;
  public static readonly API_ORDER_MARKS = (id: string) => `/api/v1/orders/${id}/marks`;
  public static readonly API_ORDER_MARK_BY_ID = (orderId: string, markId: string) => `/api/v1/orders/${orderId}/marks/${markId}`;
  
  // 票券操作 API 端點
  public static readonly API_TICKET_CANCEL_VERIFICATION = (orderId: string, itemId: string, ticketId: string) => `/api/v1/orders/${orderId}/items/${itemId}/tickets/${ticketId}/cancel-verification`;
  public static readonly API_TICKET_REFUND_SHOPPING_CREDIT = (orderId: string, itemId: string, ticketId: string) => `/api/v1/orders/${orderId}/items/${itemId}/tickets/${ticketId}/refund-shopping-credit`;
  public static readonly API_TICKET_VOID = (orderId: string, itemId: string, ticketId: string) => `/api/v1/orders/${orderId}/items/${itemId}/tickets/${ticketId}/void`;
  
  // 品牌相關常量
  public static readonly BRAND_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
  } as const;
  
  // 品牌 API 端點
  public static readonly API_BRANDS = '/api/v1/user/brands';
  public static readonly API_BRAND_BY_ID = (id: string) => `/api/v1/user/brands/${id}`;
  public static readonly API_BRAND_CREATE = '/api/v1/user/brands';
  public static readonly API_BRAND_UPDATE = (id: string) => `/api/v1/user/brands/${id}`;
  public static readonly API_BRAND_DELETE = (id: string) => `/api/v1/user/brands/${id}`;
  public static readonly API_BRAND_BATCH_UPDATE = '/api/v1/user/brands/batch';
  
  // 分店相關 API
  public static readonly API_BRAND_BRANCHES = (brandId: string) => `/api/v1/user/brands/${brandId}/branches`;
  public static readonly API_BRAND_BRANCH_BY_ID = (brandId: string, branchId: string) => `/api/v1/user/brands/${brandId}/branches/${branchId}`;
  public static readonly API_BRAND_BRANCH_CREATE = (brandId: string) => `/api/v1/user/brands/${brandId}/branches`;
  public static readonly API_BRAND_BRANCH_UPDATE = (brandId: string, branchId: string) => `/api/v1/user/brands/${brandId}/branches/${branchId}`;
  public static readonly API_BRAND_BRANCH_DELETE = (brandId: string, branchId: string) => `/api/v1/user/brands/${brandId}/branches/${branchId}`;
  
  // 品牌 Logo 上傳 API
  public static readonly API_BRAND_UPLOAD_LOGO = (id: string) => `/api/v1/user/brands/${id}/logo`;
  
  // 品牌 Logo 上傳限制
  public static readonly BRAND_LOGO_MAX_SIZE = 5 * 1024 * 1024; // 5MB
  public static readonly BRAND_LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  public static readonly BRAND_LOGO_MAX_WIDTH = 800; // 最大寬度
  public static readonly BRAND_LOGO_MAX_HEIGHT = 800; // 最大高度
  public static readonly BRAND_LOGO_COMPRESSION_QUALITY = 0.8; // 壓縮品質
  public static readonly BRAND_LOGO_TARGET_FORMAT: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/webp'; // 目標格式
  
  // 會員等級相關常量
  public static readonly MEMBER_LEVELS = {
    REGULAR: 'regular',
    SILVER: 'silver',
    GOLD: 'gold',
    PLATINUM: 'platinum',
    VIP: 'vip',
  } as const;

  public static readonly MEMBER_LEVEL_LABELS: Record<string, string> = {
    regular: '一般會員',
    silver: '銀卡會員',
    gold: '金卡會員',
    platinum: '白金會員',
    vip: 'VIP會員',
  };

  // 會員狀態相關常量
  public static readonly MEMBER_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    SUSPENDED: 'suspended',
    DELETED: 'deleted',
  } as const;

  public static readonly MEMBER_STATUS_LABELS: Record<string, string> = {
    active: '活躍',
    inactive: '非活躍',
    suspended: '已暫停',
    deleted: '已刪除',
  };

  // 會員 API 端點（根據 API 文檔：https://api.uuspa.com.tw/api-docs#/）
  public static readonly API_MEMBERS = '/api/members';
  public static readonly API_MEMBER_BY_ID = (id: string) => `/api/members/${id}`;
  public static readonly API_MEMBER_UPDATE = (id: string) => `/api/members/${id}`;
  
  // 會員等級配置 API 端點
  public static readonly API_MEMBER_LEVEL_CONFIGS = '/api/member-level-configs';
  public static readonly API_MEMBER_LEVEL_CONFIG_BY_ID = (id: string) => `/api/member-level-configs/${id}`;
  public static readonly API_MEMBER_LEVEL_CONFIG_CREATE = '/api/member-level-configs';
  public static readonly API_MEMBER_LEVEL_CONFIG_UPDATE = (id: string) => `/api/member-level-configs/${id}`;
  public static readonly API_MEMBER_LEVEL_CONFIG_DELETE = (id: string) => `/api/member-level-configs/${id}`;
  
  // 會員等級升級規則 API 端點
  public static readonly API_MEMBER_LEVEL_UPGRADE_RULES = '/api/member-level-upgrade-rules';
  public static readonly API_MEMBER_LEVEL_UPGRADE_RULE_BY_ID = (id: string) => `/api/member-level-upgrade-rules/${id}`;
  public static readonly API_MEMBER_LEVEL_UPGRADE_RULE_CREATE = '/api/member-level-upgrade-rules';
  public static readonly API_MEMBER_LEVEL_UPGRADE_RULE_UPDATE = (id: string) => `/api/member-level-upgrade-rules/${id}`;
  public static readonly API_MEMBER_LEVEL_UPGRADE_RULE_DELETE = (id: string) => `/api/member-level-upgrade-rules/${id}`;
  
  // 會員等級權益 API 端點
  public static readonly API_MEMBER_LEVEL_BENEFITS = '/api/member-level-benefits';
  public static readonly API_MEMBER_LEVEL_BENEFIT_BY_ID = (id: string) => `/api/member-level-benefits/${id}`;
  public static readonly API_MEMBER_LEVEL_BENEFIT_CREATE = '/api/member-level-benefits';
  public static readonly API_MEMBER_LEVEL_BENEFIT_UPDATE = (id: string) => `/api/member-level-benefits/${id}`;
  public static readonly API_MEMBER_LEVEL_BENEFIT_DELETE = (id: string) => `/api/member-level-benefits/${id}`;
  
  // 會員等級歷史 API 端點
  public static readonly API_MEMBER_LEVEL_HISTORY = '/api/member-level-history';
  public static readonly API_MEMBER_LEVEL_STATISTICS = '/api/member-level-statistics';
  public static readonly API_MEMBER_DELETE = (id: string) => `/api/members/${id}`;
  public static readonly API_MEMBER_RESTORE = (id: string) => `/api/members/${id}/restore`;
  public static readonly API_MEMBER_BLACKLIST_ADD = (id: string) => `/api/members/${id}/blacklist`;
  public static readonly API_MEMBER_BLACKLIST_REMOVE = (id: string) => `/api/members/${id}/blacklist`;
  public static readonly API_MEMBER_UPDATE_POINTS = (id: string) => `/api/members/${id}/points`;
  public static readonly API_MEMBER_UPDATE_LEVEL = (id: string) => `/api/members/${id}/level`;
  
  // 會員標籤 API 端點
  public static readonly API_MEMBER_TAGS = '/api/member-tags';
  public static readonly API_MEMBER_TAG_BY_ID = (id: string) => `/api/member-tags/${id}`;
  public static readonly API_MEMBER_TAG_CREATE = '/api/member-tags';
  
  // 財務管理 API 端點
  public static readonly API_FINANCE_TRANSACTIONS = '/api/v1/finance/transactions';
  public static readonly API_FINANCE_TRANSACTION_BY_ID = (id: string) => `/api/v1/finance/transactions/${id}`;
  public static readonly API_FINANCE_TRANSACTION_STATISTICS = '/api/v1/finance/transactions/statistics';
  
  public static readonly API_FINANCE_INVOICES = '/api/v1/finance/invoices';
  public static readonly API_FINANCE_INVOICE_BY_ID = (id: string) => `/api/v1/finance/invoices/${id}`;
  public static readonly API_FINANCE_INVOICE_DOWNLOAD = (id: string) => `/api/v1/finance/invoices/${id}/download`;
  
  public static readonly API_FINANCE_REFUNDS = '/api/v1/finance/refunds';
  public static readonly API_FINANCE_REFUND_BY_ID = (id: string) => `/api/v1/finance/refunds/${id}`;
  public static readonly API_FINANCE_REFUND_STATISTICS = '/api/v1/finance/refunds/statistics';
  
  public static readonly API_FINANCE_REPORTS = '/api/v1/finance/reports';
  public static readonly API_FINANCE_REPORTS_EXPORT = '/api/v1/finance/reports/export';
  
  // 財務管理權限常量
  public static readonly PERMISSIONS_FINANCE = 'finance';
  public static readonly PERMISSIONS_FINANCE_TRANSACTIONS_VIEW = 'finance:transactions:view';
  public static readonly PERMISSIONS_FINANCE_INVOICES_MANAGE = 'finance:invoices:manage';
  public static readonly PERMISSIONS_FINANCE_REFUNDS_MANAGE = 'finance:refunds:manage';
  public static readonly PERMISSIONS_FINANCE_REPORTS_VIEW = 'finance:reports:view';
  public static readonly API_MEMBER_TAG_UPDATE = (id: string) => `/api/member-tags/${id}`;
  public static readonly API_MEMBER_TAG_DELETE = (id: string) => `/api/member-tags/${id}`;
  public static readonly API_MEMBER_TAG_MEMBERS = (tagId: string) => `/api/member-tags/${tagId}/members`;
  public static readonly API_MEMBER_ADD_TAGS = (memberId: string) => `/api/members/${memberId}/tags`;
  public static readonly API_MEMBER_REMOVE_TAGS = (memberId: string) => `/api/members/${memberId}/tags`;
  public static readonly API_MEMBER_BATCH_TAG_OPERATION = '/api/members/batch-tags';

  // 合約相關常量
  public static readonly CONTRACT_STATUS = {
    ACTIVE: 'active',
    EXPIRED: 'expired',
    DRAFT: 'draft',
    PENDING: 'pending',
  } as const;
  
  public static readonly CONTRACT_TYPE = {
    COURSE: 'course',
    ADVERTISING_CREDIT: 'advertising_credit',
    ADVERTISING_SPACE: 'advertising_space',
  } as const;
  
  public static readonly CONTRACT_LEVEL = {
    A: 'A',
    B: 'B',
    C: 'C',
  } as const;
  
  public static readonly CERTIFICATE_TYPE = {
    INVOICE: 'invoice',
    RECEIPT: 'receipt',
    NONE: 'none',
  } as const;
  
  // 合約 API 端點
  public static readonly API_CONTRACTS = '/api/v1/contracts';
  public static readonly API_CONTRACT_BY_ID = (id: string) => `/api/v1/contracts/${id}`;
  public static readonly API_CONTRACT_CREATE = '/api/v1/contracts';
  public static readonly API_CONTRACT_UPDATE = (id: string) => `/api/v1/contracts/${id}`;
  public static readonly API_CONTRACT_DELETE = (id: string) => `/api/v1/contracts/${id}`;
  public static readonly API_CONTRACT_BATCH_UPDATE = '/api/v1/contracts/batch';
  public static readonly API_CONTRACT_STATISTICS = '/api/v1/contracts/statistics';
  
  // 合約檔案 API 端點
  public static readonly API_CONTRACT_FILE_UPLOAD = (contractId: string) => `/api/contracts/${contractId}/files/upload`;
  public static readonly API_CONTRACT_FILE_BATCH_UPLOAD = (contractId: string) => `/api/contracts/${contractId}/files/batch-upload`;
  public static readonly API_CONTRACT_FILES = (contractId: string) => `/api/contracts/${contractId}/files`;
  public static readonly API_CONTRACT_FILE_DOWNLOAD = (contractId: string, fileId: string) => `/api/contracts/${contractId}/files/${fileId}/download`;
  public static readonly API_CONTRACT_FILE_DELETE = (contractId: string, fileId: string) => `/api/contracts/${contractId}/files/${fileId}`;
  
  // 合約檔案相關常量
  public static readonly CONTRACT_FILE_MAX_SIZE = 10 * 1024 * 1024; // 10MB
  public static readonly CONTRACT_FILE_BATCH_MAX_SIZE = 50 * 1024 * 1024; // 50MB
  public static readonly CONTRACT_FILE_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  
  // 加密相關（AES_KEY 從環境變數讀取，請在 .env.local 設定 VITE_AES_KEY）
  public static readonly AES_KEY = import.meta.env['VITE_AES_KEY'] as string;
  public static readonly ENCRYPTION_METHOD = 'AES-ECB-PKCS7';
  
  // WebSocket 相關
  //public static readonly WS_BASE_URL = 'wss://ws.uuspa.com';
  //public static readonly WS_PATH = '/socketio/socket.io';
  //public static readonly WS_NAMESPACE = '/aile';
  
  // 設備/語言/頻道常量（定義移至 utils/constants/device.constants.ts）
  public static readonly DEVICE_TYPES = _DEVICE_TYPES;
  public static readonly CHANNELS = _CHANNELS;
  public static readonly SUPPORTED_LANGUAGES = _SUPPORTED_LANGUAGES;
  
  // 權限相關常量
  // 會員等級權限
  public static readonly PERMISSIONS_MEMBER_LEVEL_MANAGEMENT = 'member_level:manage';
  
  public static readonly PERMISSIONS = {
    // 電商功能
    PRODUCTS: 'products',
    CATEGORIES: 'categories',
    PAGES: 'pages',
    MARKETING: 'marketing',
    ORDERS: 'orders',
    MEMBERS: 'members',
    BANNERS: 'banners',
    COURSES: 'courses',
    
    // 業務功能
    BRANDS: 'brands',
    CONTRACTS: 'contracts',
    SCHEDULING: 'scheduling',
    RECONCILIATION: 'reconciliation',
    VERIFICATION: 'verification',
    
    // 客服功能
    CUSTOMER_SERVICE: 'customer_service',
    CUSTOMER_SERVICE_SUPPLEMENTARY_VERIFICATION: 'customer_service_supplementary_verification',
    CUSTOMER_SERVICE_CANCEL_VERIFICATION: 'customer_service_cancel_verification',
    CUSTOMER_SERVICE_REFUND: 'customer_service_refund',
    CUSTOMER_SERVICE_TICKET_SEARCH: 'customer_service_ticket_search',
    CUSTOMER_SERVICE_BATCH_OPERATIONS: 'customer_service_batch_operations',
    
    // 財務功能
    FINANCE: 'finance',
    FINANCE_TRANSACTIONS_VIEW: 'finance:transactions:view',
    FINANCE_INVOICES_MANAGE: 'finance:invoices:manage',
    FINANCE_REFUNDS_MANAGE: 'finance:refunds:manage',
    FINANCE_REPORTS_VIEW: 'finance:reports:view',
    
    // 票券管理
    TICKETS: 'tickets',
    
    // 購物金管理
    SHOPPING_CREDITS: 'shopping_credits',
    SHOPPING_CREDITS_ISSUE: 'shopping_credits:issue',
    SHOPPING_CREDITS_VIEW: 'shopping_credits:view',
    SHOPPING_CREDITS_REPORTS: 'shopping_credits:reports',
    
    // 優選店家
    FEATURED_STORES: 'featured_stores',
    
    // 系統功能
    PERFORMANCE: 'performance',
  } as const;
  
  // 權限管理 API
  public static readonly API_USER_PERMISSIONS_UPDATE = '/api/v1/auth/user/permissions/update';
  public static readonly API_USER_PERMISSIONS_GET = '/api/v1/auth/user/permissions';
  
  // 商品相關常量
  public static readonly PRODUCT_STATUS = {
    ON_SALE: 'on_sale',
    OFF_SALE: 'off_sale',
    DRAFT: 'draft',
    DELETED: 'deleted',
  } as const;
  
  // 商品 API 端點
  public static readonly API_PRODUCTS = '/api/v1/products';
  public static readonly API_PRODUCT_BY_ID = (id: string) => `/api/v1/products/${id}`;
  public static readonly API_PRODUCT_CREATE = '/api/v1/products';
  public static readonly API_PRODUCT_UPDATE = (id: string) => `/api/v1/products/${id}`;
  public static readonly API_PRODUCT_DELETE = (id: string) => `/api/v1/products/${id}`;
  public static readonly API_PRODUCT_BATCH_UPDATE = '/api/v1/products/batch';
  public static readonly API_PRODUCT_BATCH_DELETE = '/api/v1/products/batch/delete';
  public static readonly API_PRODUCT_UPLOAD_IMAGE = (id: string) => `/api/v1/products/${id}/images`;
  
  // 商品分類相關常量
  public static readonly CATEGORY_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
  } as const;
  
  public static readonly CATEGORY_MAX_LEVEL = 3; // 最多 3 層分類
  
  // 商品分類 API 端點
  public static readonly API_CATEGORIES = '/api/v1/product-categories';
  public static readonly API_CATEGORY_BY_ID = (id: string) => `/api/v1/product-categories/${id}`;
  public static readonly API_CATEGORY_CREATE = '/api/v1/product-categories';
  public static readonly API_CATEGORY_UPDATE = (id: string) => `/api/v1/product-categories/${id}`;
  public static readonly API_CATEGORY_DELETE = (id: string) => `/api/v1/product-categories/${id}`;
  public static readonly API_CATEGORY_BATCH_UPDATE = '/api/v1/product-categories/batch';
  public static readonly API_CATEGORY_BATCH_SORT = '/api/v1/product-categories/batch/sort';
  public static readonly API_CATEGORY_STATISTICS = '/api/v1/product-categories/statistics';
  public static readonly API_CATEGORY_PRODUCTS = (categoryId: string) => `/api/v1/product-categories/${categoryId}/products`;
  public static readonly API_CATEGORY_PRODUCT_ADD = (categoryId: string) => `/api/v1/product-categories/${categoryId}/products`;
  public static readonly API_CATEGORY_PRODUCT_REMOVE = (categoryId: string, productId: string) => `/api/v1/product-categories/${categoryId}/products/${productId}`;
  public static readonly API_CATEGORY_PRODUCT_BATCH_ADD = (categoryId: string) => `/api/v1/product-categories/${categoryId}/products/batch`;
  
  // Banner 相關常量
  public static readonly BANNER_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    DRAFT: 'draft',
  } as const;

  // Banner API 端點
  public static readonly API_BANNERS = '/api/v1/banners';
  public static readonly API_BANNER_BY_ID = (id: string) => `/api/v1/banners/${id}`;
  public static readonly API_BANNER_CREATE = '/api/v1/banners';
  public static readonly API_BANNER_UPDATE = (id: string) => `/api/v1/banners/${id}`;
  public static readonly API_BANNER_DELETE = (id: string) => `/api/v1/banners/${id}`;
  public static readonly API_BANNER_UPDATE_STATUS = (id: string) => `/api/v1/banners/${id}/status`;

  // 搜尋相關常量（定義移至 utils/constants/ui.constants.ts）
  public static readonly SEARCH_TIMEOUT = _SEARCH_TIMEOUT;
  
  // 行銷活動相關常量
  public static readonly MARKETING_STATUS = {
    DRAFT: 'draft',
    ACTIVE: 'active',
    PAUSED: 'paused',
    ENDED: 'ended',
    SCHEDULED: 'scheduled',
  } as const;

  public static readonly MARKETING_TYPE = {
    DISCOUNT: 'discount',
    COUPON: 'coupon',
    FLASH_SALE: 'flash_sale',
    BUNDLE: 'bundle',
    REFERRAL: 'referral',
    OTHER: 'other',
  } as const;

  public static readonly DISCOUNT_TYPE = {
    PERCENTAGE: 'percentage',
    COMBO_PRICE: 'combo_price',
    AMOUNT: 'amount',
    GIFT: 'gift',
  } as const;

  public static readonly TARGET_AUDIENCE = {
    ALL: 'all',
    MEMBERS: 'members',
    TAGGED: 'tagged',
  } as const;

  // 行銷活動 API 端點
  public static readonly API_MARKETING = '/api/v1/marketing';
  public static readonly API_MARKETING_BY_ID = (id: string) => `/api/v1/marketing/${id}`;
  public static readonly API_MARKETING_CREATE = '/api/v1/marketing';
  public static readonly API_MARKETING_UPDATE = (id: string) => `/api/v1/marketing/${id}`;
  public static readonly API_MARKETING_DELETE = (id: string) => `/api/v1/marketing/${id}`;
  public static readonly API_MARKETING_BATCH_UPDATE = '/api/v1/marketing/batch';
  public static readonly API_MARKETING_DUPLICATE = (id: string) => `/api/v1/marketing/${id}/duplicate`;
  public static readonly API_MARKETING_STATISTICS = '/api/v1/marketing/statistics';
  public static readonly API_MARKETING_PRODUCTS = (id: string) => `/api/v1/marketing/${id}/products`;
  
  // 購物金管理 API 端點
  public static readonly API_SHOPPING_CREDIT_ISSUE = '/api/v1/shopping-credits/issue';
  public static readonly API_SHOPPING_CREDIT_USAGE = '/api/v1/shopping-credits/usage';
  public static readonly API_SHOPPING_CREDIT_REPORTS = '/api/v1/shopping-credits/reports';
  public static readonly API_SHOPPING_CREDIT_MEMBERS = '/api/v1/shopping-credits/members';
  
  // 預設值（定義移至 utils/constants/ui.constants.ts）
  public static readonly DEFAULT_LANGUAGE = _DEFAULT_LANGUAGE;
  public static readonly DEFAULT_THEME = _DEFAULT_THEME;
  public static readonly DEFAULT_TIMEZONE = _DEFAULT_TIMEZONE;
}
